import type { PlaygroundHistoryScope } from '@shared/reference-assets'
import type { GenerationWarning } from '@shared/scale-references'
import type { Character, GeminiImageGenerationSettings, PlaygroundJob } from '@shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { captureGenerationSession, trackGeneration } from '@/lib/generation-notifications'
import { referenceBuilderRoute } from '@/lib/utils/playground-runs'

import {
  api,
  type CreatePlaygroundImageRunRequest,
  type PlaygroundHistoryFeedEntry,
  type PlaygroundRunResponse
} from './api'
import { getProcessingRefetchInterval } from './query-helpers'

// Extended PlaygroundJob type with computed cost added by the status handler.
export interface PlaygroundJobWithUrls extends PlaygroundJob {
  geminiCallCount?: number
  estimatedCostUsd?: number
}

export interface PlaygroundHistoryItem {
  generationWarnings?: GenerationWarning[]
  failure?: PlaygroundJob['failure']
  errorMessage?: string
  jobId: string
  runId?: string
  experimentId?: string
  variantIndex?: number
  toolType: string
  character?: Character
  status: string
  userInput?: string
  outputImageKey?: string
  previewUrl?: string
  createdAt: string
  updatedAt: string
  duration?: number
  geminiImageSettings?: GeminiImageGenerationSettings
  geminiCallCount?: number
  retryCount?: number
  totalTokens?: number
  estimatedCostUsd?: number
}

// Query keys for playground
export const playgroundKeys = {
  all: ['playground'] as const,
  status: (jobId: string) => [...playgroundKeys.all, 'status', jobId] as const,
  historyFeed: (creator = '', scope?: PlaygroundHistoryScope) =>
    [...playgroundKeys.all, 'history-feed', ...(scope ? [scope] : []), ...(creator ? [creator] : [])] as const,
  run: (runId: string, includeDebug: boolean) => [...playgroundKeys.all, 'run', runId, includeDebug] as const
}

/**
 * Hook to get playground job status
 * Auto-refreshes while job is processing
 */
export function usePlaygroundStatus(jobId: string | null) {
  return useQuery<PlaygroundJobWithUrls>({
    queryKey: playgroundKeys.status(jobId ?? ''),
    queryFn: () => {
      if (!jobId) throw new Error('Job ID is required')
      return api.getPlaygroundStatus(jobId)
    },
    enabled: !!jobId,
    refetchInterval: (query) => getProcessingRefetchInterval(query.state.data?.status, query.state.data?.updatedAt)
  })
}

export function usePlaygroundHistoryFeed(creator = '', scope: PlaygroundHistoryScope = 'playground') {
  return useQuery<PlaygroundHistoryFeedEntry[]>({
    queryKey: playgroundKeys.historyFeed(creator, scope),
    queryFn: async () => (await api.getPlaygroundHistoryFeed(creator, scope)).entries,
    staleTime: 30000
  })
}

export function usePlaygroundRun(runId: string | null, includeDebug = false) {
  return useQuery<PlaygroundRunResponse>({
    queryKey: playgroundKeys.run(runId ?? '', includeDebug),
    queryFn: () => {
      if (!runId) throw new Error('Run ID is required')
      return api.getPlaygroundRun(runId, includeDebug)
    },
    enabled: !!runId,
    // Switching on the request viewer changes only the response projection for
    // the same run. Retain its current gallery/form data while that projection
    // is fetched so the page never briefly unmounts and flashes.
    placeholderData: (previousData, previousQuery) => (previousQuery?.queryKey[2] === runId ? previousData : undefined),
    refetchInterval: (query) => {
      const status = query.state.data?.run.status
      return status === 'pending' || status === 'processing' ? 2500 : false
    }
  })
}

export function useCreateCharacterNanobananaRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.createCharacterNanobananaRun,
    onMutate: captureGenerationSession,
    onSuccess: (response, _, session) => {
      trackGeneration(
        {
          kind: 'playground',
          id: response.run.runId,
          href: `/playground/character-nanobanana?runId=${response.run.runId}`,
          label: 'Character generation'
        },
        session
      )
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.historyFeed() })
    }
  })
}

export function useCreatePlaygroundImageRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreatePlaygroundImageRunRequest) => api.createPlaygroundImageRun(data),
    onMutate: captureGenerationSession,
    onSuccess: (response, _, session) => {
      trackGeneration(
        {
          kind: 'playground',
          id: response.run.runId,
          href: `/playground/${response.run.toolType}?runId=${response.run.runId}`,
          label: 'Image generation'
        },
        session
      )
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.historyFeed() })
    }
  })
}

export function useDeletePlaygroundRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.deletePlaygroundRun,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.historyFeed() })
    }
  })
}

/**
 * Hook to delete a legacy playground job
 */
export function useDeletePlaygroundJob() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (jobId: string) => api.deletePlaygroundJob(jobId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.historyFeed() })
    }
  })
}

export function useCreateReferenceBuilderRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.createReferenceBuilderRun,
    onMutate: captureGenerationSession,
    onSuccess: (response, _, session) => {
      trackGeneration(
        {
          kind: 'playground',
          id: response.run.runId,
          href: `${referenceBuilderRoute(response.run.inputSnapshot?.referenceBuilder?.kind)}?runId=${response.run.runId}`,
          label: 'Reference generation'
        },
        session
      )
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.historyFeed() })
    }
  })
}
